import React, { useRef, useEffect } from "react";
import {
  HStack,
  Input,
  FormControl,
  FormLabel,
  FormErrorMessage,
  useColorModeValue,
} from "@chakra-ui/react";

interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  isDisabled?: boolean;
  isInvalid?: boolean;
  label?: string;
  errorMessage?: string;
  autoFocus?: boolean;
}

const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  isDisabled = false,
  isInvalid = false,
  label = "کد تایید",
  errorMessage,
  autoFocus = true,
}) => {
  const inputBg = useColorModeValue("white", "gray.700");
  const inputBorder = useColorModeValue("gray.300", "gray.600");
  const focusBorder = useColorModeValue("brand.500", "brand.300");
  const errorBorder = useColorModeValue("red.500", "red.300");

  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const digits = value.padEnd(5, " ").split("").slice(0, 5);

  // Auto-focus first empty input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Handle input change - only allow filling from left to right
  const handleChange = (index: number, newValue: string) => {
    if (isDisabled) return;

    // Only allow numeric input
    const numericValue = newValue.replace(/\D/g, "");
    if (!numericValue) return;

    // Get the last digit entered (in case of paste)
    const digit = numericValue.slice(-1);

    // Check if user can fill this position (all previous must be filled)
    const canFillPosition =
      index === 0 || digits.slice(0, index).every((d) => d !== " ");

    if (!canFillPosition) {
      // Focus on first empty input instead
      const firstEmpty = digits.findIndex((d) => d === " ");
      if (firstEmpty !== -1) {
        setTimeout(() => {
          inputRefs.current[firstEmpty]?.focus();
        }, 0);
      }
      return;
    }

    // Update the value
    const newDigits = [...digits];
    newDigits[index] = digit;
    const newOtp = newDigits.join("").trim();
    onChange(newOtp);

    // Auto-focus next input if available (defer to next tick)
    if (index < 4) {
      setTimeout(() => {
        inputRefs.current[index + 1]?.focus();
      }, 0);
    }
  };

  // Handle backspace
  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace") {
      e.preventDefault();

      // If current digit is filled, clear it
      if (digits[index] !== " ") {
        const newDigits = [...digits];
        newDigits[index] = " ";
        const newOtp = newDigits.join("").trim();
        onChange(newOtp);
      } else if (index > 0) {
        // If current is empty, move to previous and clear it
        const newDigits = [...digits];
        newDigits[index - 1] = " ";
        const newOtp = newDigits.join("").trim();
        onChange(newOtp);
        setTimeout(() => {
          inputRefs.current[index - 1]?.focus();
        }, 0);
      }
    }
  };

  // Handle paste event
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pasteData = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 5);
    if (pasteData) {
      onChange(pasteData);
      // Focus on next empty input or last input
      const nextIndex = Math.min(pasteData.length, 4);
      setTimeout(() => {
        inputRefs.current[nextIndex]?.focus();
      }, 0);
    }
  };

  // Handle click - only allow clicking on filled inputs or first empty
  const handleFocus = (index: number) => {
    const firstEmpty = digits.findIndex((d) => d === " ");

    // If clicking on an empty input that's not the first empty, redirect focus
    if (digits[index] === " " && firstEmpty !== -1 && firstEmpty < index) {
      setTimeout(() => {
        inputRefs.current[firstEmpty]?.focus();
      }, 0);
    }
  };

  return (
    <FormControl isInvalid={isInvalid}>
      {label && (
        <FormLabel textAlign="center" fontSize="lg" fontWeight="bold">
          {label}
        </FormLabel>
      )}
      <HStack justify="center" spacing={3} onPaste={handlePaste} dir="ltr">
        {[0, 1, 2, 3, 4].map((index) => (
          <Input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            maxLength={1}
            dir="ltr"
            value={digits[index] === " " ? "" : digits[index]}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onFocus={() => handleFocus(index)}
            isDisabled={isDisabled}
            bg={inputBg}
            borderColor={isInvalid ? errorBorder : inputBorder}
            _focus={{
              borderColor: isInvalid ? errorBorder : focusBorder,
              boxShadow: `0 0 0 1px ${isInvalid ? errorBorder : focusBorder}`,
            }}
            _hover={{
              borderColor: isInvalid ? errorBorder : focusBorder,
            }}
            fontSize="2xl"
            fontWeight="bold"
            width="60px"
            height="60px"
            textAlign="center"
            borderRadius="lg"
            transition="all 0.2s"
            placeholder="•"
          />
        ))}
      </HStack>
      {isInvalid && errorMessage && (
        <FormErrorMessage justifyContent="center" mt={2}>
          {errorMessage}
        </FormErrorMessage>
      )}
    </FormControl>
  );
};

export default OtpInput;
